import { parseEuroToCents, type ContractDetail, type ContractExternalItem, type ContractInput, type ContractPdfFacts, type ContractProjectsInput, type Lookups } from '@ft/shared';
import { useCallback, useState } from 'react';
import { useI18n } from '../i18n';
import { getAreaId, getAreaName } from '../lib/area';
import { upload } from '../lib/download';
import { centsToField, eur2 } from '../lib/format';
import { api } from '../lib/http';
import { Modal } from './ui';

interface PLine { key: number; projectId: string; amount: string }
interface YLine { key: number; year: string; amount: string; ordered: string; billed: string }
interface ELine { key: number; area: string; amount: string; date: string; note: string }

const NEW = '__new__';

/**
 * Hauptvertrag anlegen (contract = null) oder bearbeiten.
 * Werte von Hand oder aus der Vertrags-PDF (auch eingescannt, dann per Texterkennung); die PDF wird beim Speichern abgelegt.
 * Verträge gelten für alle Bereiche: Im Formular ordnet man nur Projekte des gewählten Bereichs zu. Kopf, Ablage und Abrufe
 * anderer intive-Bereiche ändern nur wer angelegt hat, Admins und Delivery Heads (contract.mayEdit); sonst nur Projekte.
 */
export function ContractForm({ contract, lookups, onClose, onSaved, presetProjectId }: { contract: ContractDetail | null; lookups: Lookups; onClose: () => void; onSaved: (id: string) => void; presetProjectId?: string }) {
  const { t, lang } = useI18n();
  const preset = presetProjectId ? lookups.projects.find((p) => p.id === presetProjectId) : undefined;
  const area = getAreaId();
  const mayEdit = contract ? contract.mayEdit : true;
  const mayAssign = contract ? contract.mayAssign : true;
  // Kunde liegt im Bereich, in dem der Vertrag angelegt wurde; aus anderen Bereichen nicht änderbar
  const homeHere = !contract || contract.homeArea.id === area;
  const otherProjects = (contract?.projects ?? []).filter((p) => p.area.id !== area);
  const [name, setName] = useState(contract?.name ?? '');
  const [number, setNumber] = useState(contract?.contractNumber ?? '');
  const [customerId, setCustomerId] = useState(contract?.customer.id ?? preset?.customerId ?? '');
  const [newCustomer, setNewCustomer] = useState('');
  const [from, setFrom] = useState(contract?.validFrom ?? '');
  const [to, setTo] = useState(contract?.validTo ?? '');
  const [budget, setBudget] = useState(contract?.budgetSet && contract.budgetCents !== null ? centsToField(contract.budgetCents) : '');
  const [link, setLink] = useState(contract?.sharepointUrl ?? '');
  const [note, setNote] = useState(contract?.note ?? '');
  const [plines, setPlines] = useState<PLine[]>(() => {
    const mine = (contract?.projects ?? []).filter((p) => p.area.id === area);
    return mine.length ? mine.map((p, i) => ({ key: i, projectId: p.project.id, amount: p.budgetCents !== null ? centsToField(p.budgetCents) : '' }))
      : [{ key: 0, projectId: preset?.id ?? '', amount: '' }];
  });
  const [elines, setElines] = useState<ELine[]>(() => (contract?.external ?? []).map((e, i) => ({ key: i, area: e.areaName, amount: centsToField(e.amountCents), date: e.date ?? '', note: e.note ?? '' })));
  const [ylines, setYlines] = useState<YLine[]>(() => (contract?.years ?? []).filter((y) => y.budgetCents !== null || y.manualOrderedCents !== null || y.manualBilledCents !== null)
    .map((y, i) => ({ key: i, year: String(y.year), amount: y.budgetCents !== null ? centsToField(y.budgetCents) : '', ordered: y.manualOrderedCents !== null ? centsToField(y.manualOrderedCents) : '', billed: y.manualBilledCents !== null ? centsToField(y.manualBilledCents) : '' })));
  const [pdf, setPdf] = useState<{ file: File; facts: ContractPdfFacts | null } | null>(null);
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = useCallback(() => { if (!busy && !reading) onClose(); }, [busy, reading, onClose]);

  const money = (s: string) => (s.trim() ? parseEuroToCents(s, lang) : null);
  // Im Bereich des Vertrags nur Projekte des Kunden; in anderen Bereichen gibt es eigene Kundeneinträge – dort alle Projekte
  const projects = homeHere ? lookups.projects.filter((p) => p.customerId === customerId) : lookups.projects;
  const custName = (id: string) => lookups.customers.find((c) => c.id === id)?.name ?? '';
  const extSum = elines.reduce<number>((s, e) => s + (money(e.amount) ?? 0), 0);
  const budgetC = money(budget);
  const shares = plines.filter((l) => l.projectId).map((l) => money(l.amount));
  const shareSum = shares.reduce<number>((s, c) => s + (c ?? 0), 0);
  const yearSum = ylines.reduce<number>((s, y) => s + (money(y.amount) ?? 0), 0);
  const yearManual = ylines.reduce<number>((s, y) => s + (money(y.ordered) ?? 0), 0);

  async function readPdf(file: File | undefined) {
    if (!file) return;
    setReading(true); setError(null);
    try {
      const f = await upload<ContractPdfFacts>('POST', '/api/contracts/parse-pdf', file);
      setPdf({ file, facts: f });
      if (f.contractNumber) setNumber(f.contractNumber);
      if (f.customerId && !contract) setCustomerId(f.customerId);
      if (f.validFrom) setFrom(f.validFrom);
      if (f.validTo) setTo(f.validTo);
      if (f.budgetCents !== null) setBudget(centsToField(f.budgetCents));
      if (!name.trim()) setName(file.name.replace(/\.pdf$/i, ''));
    } catch (e) {
      // Nicht lesbar: PDF trotzdem ablegen, Werte von Hand
      setPdf({ file, facts: null });
      setError((e as Error).message);
    } finally { setReading(false); }
  }

  async function save() {
    setError(null);
    if (!name.trim()) return setError(t('cf.err.name'));
    if (!customerId || (customerId === NEW && !newCustomer.trim())) return setError(t('cf.err.customer'));
    if (budget.trim() && budgetC === null) return setError(t('cf.err.amount'));
    if (plines.some((l) => l.amount.trim() && money(l.amount) === null) || ylines.some((y) => !/^\d{4}$/.test(y.year) || [y.amount, y.ordered, y.billed].some((v) => v.trim() && money(v) === null) || ![y.amount, y.ordered, y.billed].some((v) => v.trim()))) return setError(t('cf.err.amount'));
    if (link.trim() && !/^https:\/\//i.test(link.trim())) return setError(t('cf.err.link'));
    if (elines.some((e) => !e.area.trim() || money(e.amount) === null)) return setError(t('cf.err.ext'));
    const projectsIn = mayAssign ? plines.filter((l) => l.projectId).map((l) => ({ projectId: l.projectId, budgetCents: money(l.amount) })) : [];
    if (contract && !mayEdit) {
      // Nur Projekte des Bereichs zuordnen
      const body: ContractProjectsInput = { version: contract.version, projects: projectsIn };
      setBusy(true);
      try { await api('PUT', `/api/contracts/${contract.id}/projects`, body); onSaved(contract.id); } catch (e) { setError((e as Error).message); setBusy(false); }
      return;
    }
    const external: ContractExternalItem[] = elines.map((e) => ({ areaName: e.area.trim(), amountCents: money(e.amount)!, date: e.date || null, note: e.note.trim() || null }));
    const input: ContractInput = {
      ...(contract ? { version: contract.version } : {}),
      name: name.trim(), contractNumber: number.trim() || null,
      customerId: customerId === NEW ? null : customerId, newCustomerName: customerId === NEW ? newCustomer.trim() : null,
      validFrom: from || null, validTo: to || null, budgetCents: budgetC,
      sharepointUrl: link.trim() || null, note: note.trim() || null,
      projects: projectsIn,
      years: ylines.map((y) => ({ year: Number(y.year), budgetCents: money(y.amount), manualOrderedCents: money(y.ordered), manualBilledCents: money(y.billed) })),
      external,
    };
    setBusy(true);
    try {
      const r = await api<{ id: string }>(contract ? 'PUT' : 'POST', contract ? `/api/contracts/${contract.id}` : '/api/contracts', input);
      if (pdf) await upload('PUT', `/api/contracts/${r.id}/pdf`, pdf.file);
      onSaved(r.id);
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  }

  const f = pdf?.facts;
  return (
    <Modal title={contract ? (mayEdit ? t('cf.edit', { name: contract.name }) : t('cf.assign', { area: getAreaName(), name: contract.name })) : t('cf.new')} crumb={t('nav.contracts')} onClose={close} width={760}
      footer={<>
        {error && <p className="err" role="alert">{error}</p>}
        <button className="btn" onClick={close} disabled={busy}>{t('common.cancel')}</button>
        <button className="btn primary" onClick={save} disabled={busy || reading}>{busy ? t('common.saving') : t('common.save')}</button>
      </>}>
      <form className="form" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        {!mayEdit && <div className="notice full">{t('cf.headLocked')}</div>}
        {mayEdit && <>
        <label className={`drop full pdfdrop${reading ? ' busy' : ''}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); void readPdf(e.dataTransfer.files[0]); }}>
          {reading ? t('cf.reading') : pdf ? t('cf.pdfChosen', { file: pdf.file.name }) : contract?.pdfName ? t('cf.pdfReplace', { file: contract.pdfName }) : t('cf.drop')}
          <input type="file" accept="application/pdf,.pdf" hidden disabled={reading} onChange={(e) => { const x = e.target.files?.[0]; e.target.value = ''; void readPdf(x); }} />
        </label>
        {f && (
          <div className="notice full">
            <div>{t('cf.pdfCheck')}</div>
            {f.ocr && <div>{t('pdf.ocr')}</div>}
            {f.budgetCents === null && <div>{t('cf.pdfNoBudget')}</div>}
          </div>
        )}
        <div className="fld full">
          <label htmlFor="cf-name">{t('cf.name')} <span className="req">*</span></label>
          <input id="cf-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('cf.namePh')} autoComplete="off" />
        </div>
        <div className="fld">
          <label htmlFor="cf-no">{t('cf.number')}</label>
          <input id="cf-no" className="mono" value={number} onChange={(e) => setNumber(e.target.value)} autoComplete="off" />
        </div>
        <div className="fld">
          <label htmlFor="cf-c">{t('common.customer')} <span className="req">*</span></label>
          {homeHere ? (
            <select id="cf-c" value={customerId} onChange={(e) => { setCustomerId(e.target.value); setPlines((ls) => ls.map((l) => ({ ...l, projectId: '' }))); }}>
              <option value="">{t('common.choose')}</option>
              {lookups.customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              <option value={NEW}>{t('cf.newCustomer')}</option>
            </select>
          ) : <input id="cf-c" value={contract!.customer.name} disabled />}
          {!homeHere && <span className="help">{t('cf.customerHome', { area: contract!.homeArea.name })}</span>}
          {homeHere && !lookups.customers.length && customerId !== NEW && <span className="help">{t('cf.noCustomers')}</span>}
        </div>
        {customerId === NEW && (
          <div className="fld full">
            <label htmlFor="cf-nc">{t('cf.newCustomerName')} <span className="req">*</span></label>
            <input id="cf-nc" value={newCustomer} onChange={(e) => setNewCustomer(e.target.value)} placeholder={t('cf.newCustomerPh')} autoComplete="off" autoFocus />
            <span className="help">{t('cf.newCustomerHelp')}</span>
          </div>
        )}
        <div className="fld">
          <label htmlFor="cf-from">{t('cf.from')}</label>
          <input id="cf-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="fld">
          <label htmlFor="cf-to">{t('cf.to')}</label>
          <input id="cf-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="fld full">
          <label htmlFor="cf-b">{t('cf.budget')}</label>
          <input id="cf-b" className={`r${budget.trim() && budgetC === null ? ' bad' : ''}`} inputMode="decimal" value={budget} placeholder={lang === 'en' ? '0.00' : '0,00'}
            onChange={(e) => setBudget(e.target.value)} onBlur={() => { if (budgetC !== null) setBudget(centsToField(budgetC)); }} style={{ maxWidth: 240 }} />
          <span className="help">{t('cf.budgetHelp')}</span>
        </div>

        </>}

        {mayAssign && <div className="fld full">
          <span className="flabel">{t('cf.projects')}</span>
          <div className="lines">
            {plines.map((l, i) => (
              <div className="ln" key={l.key}>
                <select aria-label={t('cf.lineProject', { n: i + 1 })} value={l.projectId} onChange={(e) => setPlines((ls) => ls.map((x) => x.key === l.key ? { ...x, projectId: e.target.value } : x))} disabled={homeHere && !customerId}>
                  <option value="">{!homeHere || customerId ? t('of.chooseProject') : t('common.chooseCustomerFirst')}</option>
                  {projects.map((p) => <option key={p.id} value={p.id} disabled={plines.some((x) => x.key !== l.key && x.projectId === p.id)}>{homeHere ? p.name : `${custName(p.customerId)} · ${p.name}`}</option>)}
                </select>
                <input aria-label={t('cf.lineShare', { n: i + 1 })} className={`r${l.amount.trim() && money(l.amount) === null ? ' bad' : ''}`} inputMode="decimal" value={l.amount} placeholder={t('cf.sharePh')}
                  onChange={(e) => setPlines((ls) => ls.map((x) => x.key === l.key ? { ...x, amount: e.target.value } : x))}
                  onBlur={() => { const c = money(l.amount); if (c !== null) setPlines((ls) => ls.map((x) => x.key === l.key ? { ...x, amount: centsToField(c) } : x)); }} />
                <button type="button" className="btn ghost" aria-label={t('cf.lineRemove', { n: i + 1 })} onClick={() => setPlines((ls) => ls.length > 1 ? ls.filter((x) => x.key !== l.key) : [{ key: l.key + 1, projectId: '', amount: '' }])}>×</button>
              </div>
            ))}
            <div className="sum">
              <button type="button" className="btn ghost" style={{ padding: 0 }} disabled={homeHere && !customerId} onClick={() => setPlines((ls) => [...ls, { key: Math.max(...ls.map((x) => x.key)) + 1, projectId: '', amount: '' }])}>{t('of.addProject')}</button>
              {shares.some((c) => c !== null) && <span>{t('cf.shareSum')} <b className="num" style={{ color: budgetC !== null && shareSum !== budgetC ? 'var(--crit)' : 'var(--fg)' }}>{eur2(shareSum)}</b></span>}
            </div>
          </div>
          <span className="help">{t('cf.projectsHelp', { area: getAreaName() })}</span>
          {otherProjects.length > 0 && <span className="help">{t('cf.otherProjects', { list: otherProjects.map((p) => `${p.project.name} (${p.area.name})`).join(', ') })}</span>}
          {budgetC !== null && shares.some((c) => c !== null) && shareSum !== budgetC && <span className="help" style={{ color: 'var(--crit)' }}>{t('cf.shareDiff', { sum: eur2(shareSum), budget: eur2(budgetC) })}</span>}
        </div>}

        {mayEdit && <>

        <div className="fld full">
          <span className="flabel">{t('cf.years')}</span>
          <div className="lines">
            {ylines.length > 0 && <div className="ln yr lnhead" aria-hidden="true"><span>{t('ct.col.year')}</span><span>{t('ct.col.yearBudget')}</span><span>{t('cf.manualOrdered')}</span><span>{t('cf.manualBilled')}</span><span /></div>}
            {ylines.map((y, i) => {
              const upd = (k: 'amount' | 'ordered' | 'billed') => (e: React.ChangeEvent<HTMLInputElement>) => setYlines((ls) => ls.map((x) => x.key === y.key ? { ...x, [k]: e.target.value } : x));
              const fmt = (k: 'amount' | 'ordered' | 'billed') => () => { const c = money(y[k]); if (c !== null) setYlines((ls) => ls.map((x) => x.key === y.key ? { ...x, [k]: centsToField(c) } : x)); };
              const bad = (v: string) => (v.trim() && money(v) === null ? ' bad' : '');
              const ph = lang === 'en' ? '0.00' : '0,00';
              return (
                <div className="ln yr" key={y.key}>
                  <input aria-label={t('cf.lineYear', { n: i + 1 })} className="num" inputMode="numeric" value={y.year} maxLength={4} onChange={(e) => setYlines((ls) => ls.map((x) => x.key === y.key ? { ...x, year: e.target.value.replace(/\D/g, '') } : x))} />
                  <input aria-label={t('cf.lineYearBudget', { n: i + 1 })} className={`r${bad(y.amount)}`} inputMode="decimal" value={y.amount} placeholder={ph} onChange={upd('amount')} onBlur={fmt('amount')} />
                  <input aria-label={t('cf.lineManualOrdered', { n: i + 1 })} className={`r${bad(y.ordered)}`} inputMode="decimal" value={y.ordered} placeholder={ph} onChange={upd('ordered')} onBlur={fmt('ordered')} />
                  <input aria-label={t('cf.lineManualBilled', { n: i + 1 })} className={`r${bad(y.billed)}`} inputMode="decimal" value={y.billed} placeholder={ph} onChange={upd('billed')} onBlur={fmt('billed')} />
                  <button type="button" className="btn ghost" aria-label={t('cf.lineRemove', { n: i + 1 })} onClick={() => setYlines((ls) => ls.filter((x) => x.key !== y.key))}>×</button>
                </div>
              );
            })}
            <div className="sum">
              <button type="button" className="btn ghost" style={{ padding: 0 }} onClick={() => setYlines((ls) => {
                const last = ls.length ? Number(ls[ls.length - 1]!.year) : Number((from || new Date().toISOString()).slice(0, 4)) - 1;
                return [...ls, { key: Math.max(0, ...ls.map((x) => x.key)) + 1, year: String((last || new Date().getFullYear() - 1) + 1), amount: '', ordered: '', billed: '' }];
              })}>{t('cf.addYear')}</button>
              {ylines.length > 0 && <span>{t('cf.yearSum')} <b className="num" style={{ color: 'var(--fg)' }}>{eur2(yearSum)}</b>{yearManual > 0 && <> · {t('cf.manualSum')} <b className="num" style={{ color: 'var(--fg)' }}>{eur2(yearManual)}</b></>}</span>}
            </div>
          </div>
          <span className="help">{t('cf.yearsHelp')}</span>
        </div>

        <div className="fld full">
          <span className="flabel">{t('cf.ext')}</span>
          <div className="lines">
            {elines.map((e, i) => (
              <div className="ln ext" key={e.key}>
                <input aria-label={t('cf.extArea', { n: i + 1 })} value={e.area} placeholder={t('cf.extAreaPh')} onChange={(ev) => setElines((ls) => ls.map((x) => x.key === e.key ? { ...x, area: ev.target.value } : x))} />
                <input aria-label={t('cf.extAmount', { n: i + 1 })} className={`r${e.amount.trim() && money(e.amount) === null ? ' bad' : ''}`} inputMode="decimal" value={e.amount} placeholder={lang === 'en' ? '0.00' : '0,00'}
                  onChange={(ev) => setElines((ls) => ls.map((x) => x.key === e.key ? { ...x, amount: ev.target.value } : x))}
                  onBlur={() => { const c = money(e.amount); if (c !== null) setElines((ls) => ls.map((x) => x.key === e.key ? { ...x, amount: centsToField(c) } : x)); }} />
                <input aria-label={t('cf.extDate', { n: i + 1 })} type="date" value={e.date} onChange={(ev) => setElines((ls) => ls.map((x) => x.key === e.key ? { ...x, date: ev.target.value } : x))} />
                <input aria-label={t('cf.extNote', { n: i + 1 })} value={e.note} placeholder={t('cf.extNotePh')} onChange={(ev) => setElines((ls) => ls.map((x) => x.key === e.key ? { ...x, note: ev.target.value } : x))} />
                <button type="button" className="btn ghost" aria-label={t('cf.lineRemove', { n: i + 1 })} onClick={() => setElines((ls) => ls.filter((x) => x.key !== e.key))}>×</button>
              </div>
            ))}
            <div className="sum">
              <button type="button" className="btn ghost" style={{ padding: 0 }} onClick={() => setElines((ls) => [...ls, { key: Math.max(0, ...ls.map((x) => x.key)) + 1, area: '', amount: '', date: '', note: '' }])}>{t('cf.addExt')}</button>
              {elines.length > 0 && <span>{t('cf.extSum')} <b className="num" style={{ color: 'var(--fg)' }}>{eur2(extSum)}</b></span>}
            </div>
          </div>
          <span className="help">{t('cf.extHelp')}</span>
        </div>

        <div className="fld full">
          <label htmlFor="cf-l">{t('cf.link')}</label>
          <input id="cf-l" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…sharepoint.com/…" autoComplete="off" />
          <span className="help">{t('cf.linkHelp')}</span>
        </div>
        <div className="fld full">
          <label htmlFor="cf-n">{t('of.note')}</label>
          <textarea id="cf-n" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        </>}
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
